import { Chip, Paper, Stack } from "@mui/material";
import AssistantMessage from "./components/AssistantMessage";
import { useCallback, useEffect, useRef, useState } from "react";
import PanelHeader from "./components/PanelHeader";
import QuestionAnswerBlock from "src/components/QuestionAnswerBlock";
import ChatInput from "src/pages/AskVida/components/ChatInput";
import { Image, PictureAsPdf } from "@mui/icons-material";

interface Props {
  onClose: () => void;
}

const ChatPanel = ({ onClose }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const [questions, setQuestions] = useState<
    { id: string; text: string; file: File | null }[]
  >([]);
  const [value, setValue] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const onScroll = useCallback(() => {
    const el = containerRef.current!;
    // within 80px of bottom = "following the stream"
    stickToBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const handleSubmit = useCallback(() => {
    const id = window.crypto.randomUUID();
    setQuestions((questions) =>
      questions.concat({
        id,
        text: value,
        file,
      }),
    );
    setValue("");
    setFile(null);

    requestAnimationFrame(() => scrollToBottom("smooth"));
  }, [file, scrollToBottom, value]);

  const onSuccess = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  useEffect(() => {
    const conversationId = window.crypto.randomUUID();
    setConversationId(conversationId);
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      if (stickToBottom.current) scrollToBottom("auto");
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollToBottom]);

  return (
    <Paper
      elevation={8}
      sx={{
        width: "50vw",
        height: "75vh",
        maxHeight: "calc(100vh - 48px)", // never taller than viewport
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden", // clip children to rounded corners
      }}
    >
      <PanelHeader onClose={onClose} />
      <Stack
        ref={containerRef}
        onScroll={onScroll}
        sx={{
          flex: 1,
          overflow: "auto",
          wordWrap: "break-word",
          borderRadius: "10px",
        }}
        gap={5}
        alignSelf={"baseline"}
        p={2}
        width={"100%"}
      >
        <AssistantMessage content="Hola I am your AI assistant Vida, how can I help you today?" />
        <Stack ref={contentRef}>
          {questions.map((question) => (
            <QuestionAnswerBlock
              conversationId={conversationId}
              question={question}
              key={question.id}
              onSuccess={onSuccess}
              agent="knowledge"
            />
          ))}
        </Stack>
      </Stack>

      <Stack sx={{ flexShrink: 0 }} p={2} gap={1}>
        {file != null ? (
          <Stack direction={"row"}>
            <Chip
              label={file.name}
              onDelete={handleRemoveFile}
              icon={
                file.type === "application/pdf" ? <PictureAsPdf /> : <Image />
              }
            />
          </Stack>
        ) : null}
        <ChatInput
          value={value}
          setValue={setValue}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
          setFile={setFile}
        />
      </Stack>
    </Paper>
  );
};

export default ChatPanel;
